import React from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, StickyNote, BookOpen, User } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkLatexSanitizer from '@/utils/remarkLatexSanitizer';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import 'katex/dist/katex.min.css';
import BookmarkIcon from './BookmarkIcon';

export default function NoteModal({
    item,
    isOpen,
    onClose,
    onNext,
    onPrev,
    hasNext,
    hasPrev,
    chapterId
}) {
    if (!item) return null;

    const getTitle = () => {
        const data = item.content_data || {};
        if (data.heading) return data.heading;
        if (data.title) return data.title;
        if (item.bookmark_type === 'user_note') return 'My Note';
        if (item.section_number === 'Teacher Notes') return 'Teacher Note';
        return 'Short Note';
    };

    const getContent = () => {
        const data = item.content_data || {};
        // Prioritize full_text, then content, then preview_text
        let content = data.full_text || data.content || data.preview_text || '';

        // Cleanup for display if needed (similar to BookmarksView getPreview logic)
        // But here we might want the full rich content if available
        return content;
    };

    const getIcon = () => {
        if (item.bookmark_type === 'user_note') return <User className="w-5 h-5 text-green-500" />;
        if (item.section_number === 'Teacher Notes') return <BookOpen className="w-5 h-5 text-blue-500" />;
        return <StickyNote className="w-5 h-5 text-yellow-500" />;
    };

    const getTypeLabel = () => {
        if (item.bookmark_type === 'user_note') return 'My Note';
        if (item.section_number === 'Teacher Notes') return 'Teacher Note';
        return 'Short Note';
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowRight' && hasNext) onNext();
        if (e.key === 'ArrowLeft' && hasPrev) onPrev();
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent
                className="max-w-2xl w-full max-h-[85vh] flex flex-col p-0 gap-0 bg-neutral-900 border-neutral-800"
                onKeyDown={handleKeyDown}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-neutral-800 bg-neutral-900 shrink-0 rounded-t-lg">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-neutral-800">
                            {getIcon()}
                        </div>
                        <div>
                            <DialogTitle className="text-lg font-semibold text-white flex items-center gap-2">
                                {getTitle()}
                            </DialogTitle>
                            <DialogDescription className="text-xs text-gray-400 flex items-center gap-2">
                                <span className="font-medium text-primary/80">{getTypeLabel()}</span>
                                {item.section_number && <span>• Section {item.section_number}</span>}
                            </DialogDescription>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <BookmarkIcon
                            chapterId={chapterId}
                            initialBookmarked={true}
                            bookmarkId={item.id}
                            size={18}
                            className="h-8 w-8 text-gray-400 hover:text-white hover:bg-neutral-800"
                        />
                        <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8 rounded-full text-gray-400 hover:text-white hover:bg-neutral-800">
                            <X size={18} />
                        </Button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 min-h-[300px]">
                    <div className="prose prose-invert prose-sm max-w-none text-gray-200 [&_strong]:text-white [&_h1]:text-white [&_h2]:text-white [&_h3]:text-white [&_code]:bg-neutral-800 [&_code]:text-gray-200 [&_pre]:bg-neutral-950 [&_pre]:p-4 [&_pre]:rounded-lg">
                        <ReactMarkdown
                            remarkPlugins={[remarkLatexSanitizer, remarkMath, remarkGfm]}
                            rehypePlugins={[rehypeRaw, [rehypeKatex, { trust: true, strict: false }]]}
                        >
                            {getContent()}
                        </ReactMarkdown>
                    </div>
                </div>

                {/* Footer / Navigation - Removed as per request */}

            </DialogContent>
        </Dialog>
    );
}
